// The toasting loop. Wires input → sim → rendering each frame. All game logic
// lives in src/sim (pure, framework-free); this scene only reads sim state and
// draws it via the render modules, plays audio, and adds juice. Phase 4c.

import Phaser from 'phaser';
import { RunState } from '../sim/run-state';
import { MAX_DT, BURN_THRESHOLD, MAX_WIND_AMP } from '../sim/constants';
import { Controller } from '../input/controller';
import { isTouchDevice } from '../input/device';
import { HeatGauge } from '../ui/heat-gauge';
import { Hud } from '../ui/hud';
import { DebugOverlay } from '../ui/debug-overlay';
import { FlameRenderer } from '../render/flame';
import { MarshmallowRenderer } from '../render/marshmallow';
import { audio } from '../audio';

export class PlayScene extends Phaser.Scene {
  private run!: RunState;
  private controller!: Controller;
  private gauge!: HeatGauge;
  private hud!: Hud;
  private debug!: DebugOverlay;

  private marshmallow!: MarshmallowRenderer;
  private flame!: FlameRenderer;
  private stick!: Phaser.GameObjects.Rectangle;
  private windCue!: Phaser.GameObjects.Text;
  private hint!: Phaser.GameObjects.Text;
  private muteIndicator!: Phaser.GameObjects.Text;

  private playTop = 0;
  private playBottom = 0;
  private columnX = 0;
  private over = false;

  constructor() {
    super({ key: 'PlayScene' });
  }

  create(): void {
    this.over = false;
    this.run = new RunState(Date.now() | 0);

    this.flame = new FlameRenderer(this, 0, 0);
    this.stick = this.add.rectangle(0, 0, 6, 12, 0xc9a36b).setOrigin(0.5, 0).setDepth(2);
    this.marshmallow = new MarshmallowRenderer(this);

    this.gauge = new HeatGauge(this, 0, 0, 0, 0);
    this.hud = new Hud(this);
    this.debug = new DebugOverlay(this);

    this.windCue = this.add
      .text(0, 0, '🌬  wind', {
        fontFamily: 'system-ui',
        fontSize: '16px',
        color: '#bcd3e8',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(12)
      .setAlpha(0);

    // Control hint, tailored to the input device; fades out after a moment.
    this.hint = this.add
      .text(0, 0, isTouchDevice() ? 'tap & drag to move' : '↑ / ↓  or  W / S to move', {
        fontFamily: 'system-ui',
        fontSize: '18px',
        color: '#b89a6a',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(12);
    this.tweens.add({ targets: this.hint, alpha: { from: 1, to: 0 }, delay: 2200, duration: 900 });

    // On-screen mute confirmation (Title shows it on its button; in-game the
    // player needs feedback too). Persistent "muted" badge while muted; a brief
    // "sound on" flash when unmuting. Audio remains the single mute source.
    this.muteIndicator = this.add
      .text(0, 0, '', { fontFamily: 'system-ui', fontSize: '16px', color: '#9aa0a6' })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(12);

    this.controller = new Controller(this, 0.62);

    // Resume audio on the first gesture in this scene (safety net in addition
    // to the Title Play button). Idempotent.
    const resume = (): void => void audio.resumeOnGesture();
    this.input.once(Phaser.Input.Events.POINTER_DOWN, resume);
    this.input.keyboard?.once('keydown', resume);

    // M toggles mute (audio is the single source of truth for mute state).
    this.input.keyboard?.on('keydown-M', () => {
      audio.toggleMute();
      this.refreshMuteIndicator(true);
    });
    this.refreshMuteIndicator(false);

    this.layout();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.layout, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, this.layout, this);
      this.controller.destroy();
      this.flame.destroy();
      this.marshmallow.destroy();
      this.gauge.destroy();
      this.hud.destroy();
      this.debug.destroy();
      audio.setSizzle(0);
    });
  }

  private layout(): void {
    const { width, height } = this.scale.gameSize;
    this.playTop = height * 0.16;
    this.playBottom = height * 0.78;
    this.columnX = width * 0.42;

    this.flame.setPosition(this.columnX, height * 0.9);

    const gaugeW = Math.max(28, Math.min(48, width * 0.06));
    const gaugeX = width - gaugeW - Math.max(16, width * 0.06);
    this.gauge.setLayout(gaugeX, this.playTop, gaugeW, this.playBottom - this.playTop);

    this.windCue.setPosition(this.columnX, this.playTop - 28);
    this.hint.setPosition(width / 2, height * 0.5);
    this.muteIndicator.setPosition(width - 16, 44); // under the burns counter

    this.controller.setPlayfield(this.playTop, this.playBottom);
    this.hud.layout();
  }

  /** Reflect the current mute state on screen. `announce` shows a transient
   *  "sound on" flash when unmuting; while muted a persistent badge stays up. */
  private refreshMuteIndicator(announce: boolean): void {
    this.tweens.killTweensOf(this.muteIndicator);
    if (audio.isMuted()) {
      this.muteIndicator.setText('🔇 muted').setAlpha(1).setVisible(true);
    } else if (announce) {
      this.muteIndicator.setText('🔊 sound on').setAlpha(1).setVisible(true);
      this.tweens.add({ targets: this.muteIndicator, alpha: 0, delay: 900, duration: 500 });
    } else {
      this.muteIndicator.setVisible(false);
    }
  }

  update(time: number, delta: number): void {
    const dt = Math.min(MAX_DT, delta / 1000);
    this.controller.update(dt);

    if (this.over) {
      this.flame.update(time);
      return;
    }

    const distance = this.controller.getTargetDistance();
    const result = this.run.applyStep(distance, dt);
    const heat = this.run.heat;
    const d = this.run.getDifficulty();

    // Position the marshmallow: distance 0 = at flame (bottom), 1 = far (top).
    const y = Phaser.Math.Linear(this.playBottom, this.playTop, distance);
    this.marshmallow.update(this.columnX, y, heat.toastedness, heat.heat, heat.burnt, time);
    this.stick.setPosition(this.columnX, y + this.marshmallow.bodyH * 0.46);

    this.flame.setIntensity(d.flameIntensity);
    this.flame.update(time);

    // Continuous sizzle: louder the closer to the flame and the hotter it is.
    const heatFactor = Phaser.Math.Clamp(heat.heat / BURN_THRESHOLD, 0, 1);
    audio.setSizzle((1 - distance) * heatFactor);

    // Wind cue fades in as the difficulty's wind amplitude rises.
    const windNorm = Phaser.Math.Clamp(d.windAmp / MAX_WIND_AMP, 0, 1);
    const targetAlpha = windNorm > 0.18 ? windNorm : 0;
    this.windCue.setAlpha(Phaser.Math.Linear(this.windCue.alpha, targetAlpha, 0.08));

    this.gauge.update(heat.heat, d.bandMin, d.bandMax, BURN_THRESHOLD);
    this.hud.update({
      score: this.run.score,
      combo: this.run.combo,
      marshmallowsDone: this.run.marshmallowsDone,
      burnsRemaining: this.run.burnsRemaining,
    });
    this.debug.update({
      heat: heat.heat,
      toastedness: heat.toastedness,
      distance,
      dt,
      flameIntensity: d.flameIntensity,
      bandMin: d.bandMin,
      bandMax: d.bandMax,
      score: this.run.score,
      fps: this.game.loop.actualFps,
    });

    if (result.event === 'toasted') {
      audio.playSfx('ding');
      this.cameras.main.flash(220, 255, 210, 122); // soft gold pop
    } else if (result.event === 'burnt') {
      audio.playSfx('burn');
      this.cameras.main.shake(220, 0.012);
      this.cameras.main.flash(260, 255, 59, 47); // red
    } else if (result.event === 'gameover') {
      audio.playSfx('gameover');
      this.endRun();
    }
  }

  private endRun(): void {
    this.over = true;
    audio.setSizzle(0);
    // Hand the final score to GameOverScene, which owns high-score submission.
    this.scene.start('GameOverScene', {
      score: this.run.score,
      marshmallowsDone: this.run.marshmallowsDone,
    });
  }
}
